import React, { useEffect, useState } from 'react';
import { authorizedRequest } from '../../api/authorizedRequest';
import Swal from 'sweetalert2';

interface Role {
  id: string;
  name: string;
}

interface EmployeeDetail {
  id: string;
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  balance: number;
  accountType: string;
  bankAccount: string;
  ssn: string;
  isActive: boolean;
  printStub: boolean;
  userId: string;
  email: string;
  roles: string[];
}

interface EmployeeModalProps {
  show: boolean;
  onClose: () => void;
  employeeId: string | null;
  onSave: (data: EmployeeDetail) => void;
}

const EmployeeModal: React.FC<EmployeeModalProps> = ({ show, onClose, employeeId, onSave }) => {
  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && employeeId) {
      fetchEmployee(employeeId);
      fetchRoles();
    }
    // eslint-disable-next-line
  }, [show, employeeId]);

  const fetchEmployee = async (id: string) => {
    setLoading(true);
    const res = await authorizedRequest({ url: `/Employee/${id}`, method: 'GET' });
    setEmployee(res.data);
    setSelectedRoles(res.data.roles || []);
    setLoading(false);
  };

  const fetchRoles = async () => {
    const res = await authorizedRequest({ url: '/Role', method: 'GET' });
    setRoles(res.data);
  };

  const handleRoleChange = (roleName: string) => {
    setSelectedRoles((prev) =>
      prev.includes(roleName)
        ? prev.filter((r) => r !== roleName)
        : [...prev, roleName]
    );
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (employee) {
      setEmployee({ ...employee, email: e.target.value });
    }
  };

  const handleDelete = async () => {
    if (!employee) return;
    // Bootstrap modal confirmation
    const modal = document.createElement('div');
    modal.className = 'modal fade';
    modal.tabIndex = -1;
    modal.innerHTML = `
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this employee? This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" id="confirmDeleteBtn">Delete</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    // @ts-ignore
    window.$(modal).modal('show');
    // Wait for user action
    modal.querySelector('#confirmDeleteBtn')?.addEventListener('click', async () => {
      const res = await authorizedRequest({
        url: `/Employee/${employee.id}`,
        method: 'DELETE',
      });
      // @ts-ignore
      window.$(modal).modal('hide');
      document.body.removeChild(modal);
      if (res.status === 200) {
        onClose();
      } else {
        alert('Failed to delete employee.');
      }
    });
    // Remove modal on cancel/close
    modal.querySelectorAll('[data-dismiss="modal"]').forEach(btn => {
      btn.addEventListener('click', () => {
        // @ts-ignore
        window.$(modal).modal('hide');
        document.body.removeChild(modal);
      });
    });
  };


  const handleSave = async () => {
    if (employee) {
      const payload = {
        id: employee.id,
        email: employee.email,
        roles: selectedRoles,
      };
      const res = await authorizedRequest({
        url: `/Employee/${employee.id}`,
        method: 'PUT',
        data: payload,
      });

      if (res.status === 200) {
        onSave({ ...employee, roles: selectedRoles });
        Swal.fire({
          position: 'top-end',
          title: 'Success!',
          text: 'Employee updated successfully!',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        onClose();
      } else {
        Swal.fire({
          position: 'top-end',
          title: 'Error!',
          text: 'Failed to update employee.',
          icon: 'error',
          timer: 5000,
          showConfirmButton: false
        });
      }
    }
  };

  if (!show) return null;

  return (
    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog" style={{ maxWidth: '1200px', width: '90%' }}>
        <div className="modal-content text-left">
          <div className="modal-header">
            <h5 className="modal-title">Employee Details</h5>
            <button type="button" className="close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            {loading || !employee ? (
               <div>Loading...</div>
            ) : (
              <form>
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Id</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.id} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Name</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.name} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Address 1</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.address1} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Address 2</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.address2} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">City</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.city} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">State</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.state} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Zip</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.zip} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Balance</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.balance} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Account Type</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.accountType} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Bank Account</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.bankAccount} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">SSN</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.ssn} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Is Active</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.isActive ? 'Yes' : 'No'} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Print Stub</label>
                        <div className="col-sm-8">
                          <input className="form-control" value={employee.printStub ? 'Yes' : 'No'} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row">
                        <label className="col-sm-4 col-form-label">User Email</label>
                        <div className="col-sm-8">
                          <input type="email" className="form-control" value={employee.email} onChange={handleEmailChange} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-sm-12 col-md-3 col-form-label">Roles</label>
                    <div className="col-sm-12 col-md-9">
                      {roles.map((role) => (
                        <div key={role.id} className="custom-control custom-checkbox mb-2">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id={`role-${role.id}`}
                            checked={selectedRoles.includes(role.name)}
                            onChange={() => handleRoleChange(role.name)}
                          />
                          <label className="custom-control-label" htmlFor={`role-${role.id}`}>{role.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-danger mr-auto" onClick={handleDelete}>
              Delete
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeModal;
